// src/components/advisor/AdvisorReadinessQueue.jsx
// §6 — La readiness devient une FILE DE TRAVAIL, pas un pourcentage moyen.
// Titre « Préparation des dossiers clients », filtre client/collection, résumé
// « N actions · M œuvres », rubriques cliquables → liste d'œuvres filtrée →
// sélection multiple → créer une mission (ou relance) groupée avec les œuvres liées.
// Le pourcentage de complétude devient secondaire.
// Réutilise le moteur readiness (utils/readiness.js). i18n FR/EN/DE inline.
import React, { useMemo, useState } from "react";
import {
  ClipboardList, ChevronRight, CheckSquare, Square, FolderPlus, Bell, X,
} from "lucide-react";
import { READINESS_DIMENSIONS, computeArtworkReadiness } from "../../utils/readiness";

const C = {
  ink: "#1A1A1A", cream: "#FAFAF8", white: "#FFFFFF", stone: "#E5E3DF",
  gray: "#6B6B6B", gold: "#C9A961", goldDark: "#8B7355",
  attnBg: "#fbf6ea", successFg: "#2c5f2d", successBg: "#f1f6f1",
};
const SERIF = "Georgia, 'Times New Roman', serif";
const SANS = "system-ui, -apple-system, 'Segoe UI', sans-serif";
const DIM = Object.fromEntries(READINESS_DIMENSIONS.map((d) => [d.key, d]));

// Rubriques §6 (ordre d'affichage) → dimension readiness + type de mission suggéré.
const RUBRICS = [
  { key: "valuation",     dim: "valuation",     missionType: "valuation" },
  { key: "documentation", dim: "documentation", missionType: "inventory" },
  { key: "provenance",    dim: "provenance",    missionType: "provenance_research" },
  { key: "conservation",  dim: "conservation",  missionType: "condition_review" },
  { key: "insurance",     dim: "insurance",     missionType: "insurance_file" },
  { key: "ready",         dim: null,            missionType: "curatorial_report" }, // dossiers prêts à produire
];
const READY_THRESHOLD = 80;

const COPY = {
  fr: {
    eyebrow: "File de travail",
    title: "Préparation des dossiers clients",
    allClients: "Tous les clients", allCollections: "Toutes les collections",
    summary: (a, w) => `${a} action${a > 1 ? "s" : ""} · ${w} œuvre${w > 1 ? "s" : ""}`,
    allCovered: "Tous les dossiers sont à jour sur votre périmètre.",
    selectAll: "Tout sélectionner", deselect: "Tout désélectionner",
    selectedN: (n) => `${n} sélectionnée${n > 1 ? "s" : ""}`,
    createMission: "Créer une mission groupée", relance: "Relancer le client",
    close: "Fermer", untitled: "Sans titre", unknownArtist: "Artiste inconnu",
    relanceTitle: (r) => `Relance — ${r}`,
    rubrics: {
      valuation: "Œuvres à valoriser", documentation: "Documents à récupérer",
      provenance: "Provenance à vérifier", conservation: "Constats d’état à mettre à jour",
      insurance: "Données d’assurance à réviser", ready: "Dossiers prêts à produire",
    },
    missionTitles: {
      valuation: "Valorisation groupée", documentation: "Audit documentaire",
      provenance: "Recherche de provenance", conservation: "Revue de conservation",
      insurance: "Revue d’assurance", ready: "Rapport patrimonial",
    },
  },
  en: {
    eyebrow: "Work queue",
    title: "Client file preparation",
    allClients: "All clients", allCollections: "All collections",
    summary: (a, w) => `${a} action${a > 1 ? "s" : ""} · ${w} artwork${w > 1 ? "s" : ""}`,
    allCovered: "Every file is up to date across your scope.",
    selectAll: "Select all", deselect: "Deselect all",
    selectedN: (n) => `${n} selected`,
    createMission: "Create a grouped mission", relance: "Follow up with client",
    close: "Close", untitled: "Untitled", unknownArtist: "Unknown artist",
    relanceTitle: (r) => `Follow-up — ${r}`,
    rubrics: {
      valuation: "Artworks to value", documentation: "Documents to collect",
      provenance: "Provenance to verify", conservation: "Condition reports to update",
      insurance: "Insurance data to review", ready: "Files ready to produce",
    },
    missionTitles: {
      valuation: "Grouped valuation", documentation: "Documentary audit",
      provenance: "Provenance research", conservation: "Conservation review",
      insurance: "Insurance review", ready: "Patrimonial report",
    },
  },
  de: {
    eyebrow: "Arbeitsliste",
    title: "Vorbereitung der Kundenakten",
    allClients: "Alle Kunden", allCollections: "Alle Sammlungen",
    summary: (a, w) => `${a} Aktion${a > 1 ? "en" : ""} · ${w} Werk${w > 1 ? "e" : ""}`,
    allCovered: "Alle Akten sind in Ihrem Bereich aktuell.",
    selectAll: "Alle auswählen", deselect: "Auswahl aufheben",
    selectedN: (n) => `${n} ausgewählt`,
    createMission: "Gebündelten Auftrag erstellen", relance: "Kunde nachfassen",
    close: "Schließen", untitled: "Ohne Titel", unknownArtist: "Unbekannter Künstler",
    relanceTitle: (r) => `Nachfassen — ${r}`,
    rubrics: {
      valuation: "Zu bewertende Werke", documentation: "Zu beschaffende Dokumente",
      provenance: "Provenienz prüfen", conservation: "Zustandsberichte aktualisieren",
      insurance: "Versicherungsdaten prüfen", ready: "Produktionsreife Akten",
    },
    missionTitles: {
      valuation: "Gebündelte Bewertung", documentation: "Dokumentenprüfung",
      provenance: "Provenienzrecherche", conservation: "Zustandsprüfung",
      insurance: "Versicherungsprüfung", ready: "Vermögensbericht",
    },
  },
};
const pick = (lang) => COPY[String(lang || "fr").slice(0, 2).toLowerCase()] || COPY.fr;

const cardBase = { background: C.white, border: `1px solid ${C.stone}`, borderRadius: 8 };
const selectStyle = {
  padding: "8px 12px", border: `1px solid ${C.stone}`, borderRadius: 7,
  background: C.white, color: C.ink, fontFamily: SANS, fontSize: 13, cursor: "pointer",
};

export default function AdvisorReadinessQueue({
  artworks = [],
  collectionNames = new Map(),
  clientNames = new Map(),
  lang = "fr",
  onCreateMission,
  busy = false,
}) {
  const tx = pick(lang);
  const [filterClient, setFilterClient] = useState("all");
  const [filterCollection, setFilterCollection] = useState("all");
  const [openRubric, setOpenRubric] = useState(null);
  const [selected, setSelected] = useState(() => new Set());

  // Options de filtre présentes dans les données
  const clientOptions = useMemo(() => {
    const ids = [...new Set(artworks.map((a) => a.client_id).filter(Boolean))];
    return ids.map((id, i) => ({ id, label: clientNames.get(id) || `Client ${i + 1}` }));
  }, [artworks, clientNames]);

  const collectionOptions = useMemo(() => {
    const ids = [...new Set(artworks.map((a) => a.collection_id).filter(Boolean))];
    return ids.map((id) => ({ id, label: collectionNames.get(id) || "—" }));
  }, [artworks, collectionNames]);

  const filtered = useMemo(() => {
    return artworks.filter((a) =>
      (filterClient === "all" || a.client_id === filterClient) &&
      (filterCollection === "all" || a.collection_id === filterCollection)
    );
  }, [artworks, filterClient, filterCollection]);

  // Concerned par rubrique (sur le périmètre filtré)
  const rubricSets = useMemo(() => {
    const map = {};
    for (const r of RUBRICS) {
      if (r.key === "ready") {
        map[r.key] = filtered.filter((a) => computeArtworkReadiness(a).score >= READY_THRESHOLD);
      } else {
        const dim = DIM[r.dim];
        map[r.key] = dim ? filtered.filter((a) => !dim.test(a)) : [];
      }
    }
    return map;
  }, [filtered]);

  const totalActions = useMemo(
    () => RUBRICS.filter((r) => r.key !== "ready").reduce((s, r) => s + (rubricSets[r.key]?.length || 0), 0),
    [rubricSets]
  );

  const openList = openRubric ? rubricSets[openRubric] || [] : [];
  const openMeta = RUBRICS.find((r) => r.key === openRubric);

  const toggle = (id) =>
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  const selectAll = () => setSelected(new Set(openList.map((a) => a.id)));
  const clearSel = () => setSelected(new Set());

  const changeRubric = (key) => {
    setOpenRubric((prev) => (prev === key ? null : key));
    setSelected(new Set());
  };

  // Déduit client/collection communs à la sélection
  const selectionScope = (ids) => {
    const rows = openList.filter((a) => ids.has(a.id));
    const clients = new Set(rows.map((a) => a.client_id).filter(Boolean));
    const colls = new Set(rows.map((a) => a.collection_id).filter(Boolean));
    return {
      clientId: filterClient !== "all" ? filterClient : (clients.size === 1 ? [...clients][0] : null),
      collectionId: filterCollection !== "all" ? filterCollection : (colls.size === 1 ? [...colls][0] : null),
    };
  };

  const buildTitle = (base, scope) => {
    const cn = scope.collectionId && collectionNames.get(scope.collectionId);
    const cln = scope.clientId && clientNames.get(scope.clientId);
    const suffix = cn || cln;
    return suffix ? `${base} — ${suffix}` : base;
  };

  const doCreate = (relance) => {
    if (!openMeta || selected.size === 0) return;
    const ids = [...selected];
    const scope = selectionScope(selected);
    const base = tx.missionTitles[openMeta.key] || tx.title;
    const title = relance ? tx.relanceTitle(base) : buildTitle(base, scope);
    onCreateMission?.({
      artworkIds: ids,
      missionType: openMeta.missionType,
      title,
      clientId: scope.clientId,
      collectionId: scope.collectionId,
      status: relance ? "waiting_client" : "draft",
      rubric: openMeta.key,
    });
    clearSel();
  };

  if (!artworks.length) return null;

  const labelFor = (a) => {
    const cn = a.collection_id && collectionNames.get(a.collection_id);
    const cln = a.client_id && clientNames.get(a.client_id);
    return [cln, cn].filter(Boolean).join(" · ");
  };

  return (
    <section style={{ ...cardBase, padding: 20, marginBottom: 28 }}>
      {/* En-tête + filtres */}
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap", alignItems: "flex-start", marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.09em", color: C.goldDark, fontFamily: SANS, fontWeight: 600, marginBottom: 4 }}>
            {tx.eyebrow}
          </div>
          <h2 style={{ fontFamily: SERIF, fontSize: 20, fontWeight: 400, color: C.ink, margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
            <ClipboardList size={19} strokeWidth={1.6} color={C.goldDark} /> {tx.title}
          </h2>
          <div style={{ fontSize: 13, color: C.gray, fontFamily: SANS, marginTop: 6 }}>
            {tx.summary(totalActions, filtered.length)}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <select value={filterClient} onChange={(e) => { setFilterClient(e.target.value); setOpenRubric(null); clearSel(); }} style={selectStyle}>
            <option value="all">{tx.allClients}</option>
            {clientOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
          <select value={filterCollection} onChange={(e) => { setFilterCollection(e.target.value); setOpenRubric(null); clearSel(); }} style={selectStyle}>
            <option value="all">{tx.allCollections}</option>
            {collectionOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        </div>
      </div>

      {/* Rubriques cliquables */}
      {totalActions === 0 && (rubricSets.ready?.length || 0) === 0 ? (
        <p style={{ fontSize: 14, color: C.successFg, fontFamily: SANS, margin: 0 }}>{tx.allCovered}</p>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(230px, 100%), 1fr))", gap: 8 }}>
          {RUBRICS.map((r) => {
            const count = rubricSets[r.key]?.length || 0;
            if (count === 0) return null;
            const isReady = r.key === "ready";
            const active = openRubric === r.key;
            return (
              <button
                key={r.key}
                type="button"
                onClick={() => changeRubric(r.key)}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
                  padding: "11px 13px", borderRadius: 7, cursor: "pointer", textAlign: "left",
                  background: active ? C.ink : (isReady ? C.successBg : C.white),
                  border: `1px solid ${active ? C.ink : C.stone}`,
                  color: active ? C.white : C.ink, fontFamily: SANS, fontSize: 13,
                }}
              >
                <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                  <span style={{
                    minWidth: 24, height: 20, padding: "0 6px", borderRadius: 999, flexShrink: 0,
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    fontSize: 11, fontWeight: 700, fontVariantNumeric: "tabular-nums",
                    background: active ? "rgba(255,255,255,0.18)" : (isReady ? "#dce8dc" : C.cream),
                    color: active ? C.white : (isReady ? C.successFg : C.goldDark),
                  }}>{count}</span>
                  <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{tx.rubrics[r.key]}</span>
                </span>
                <ChevronRight size={15} strokeWidth={1.7} style={{ flexShrink: 0, transform: active ? "rotate(90deg)" : "none", transition: "transform 0.15s" }} />
              </button>
            );
          })}
        </div>
      )}

      {/* Liste filtrée + sélection multiple */}
      {openRubric && openList.length > 0 && (
        <div style={{ marginTop: 16, border: `1px solid ${C.stone}`, borderRadius: 8, overflow: "hidden" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "10px 12px", background: C.cream, borderBottom: `1px solid ${C.stone}` }}>
            <button type="button" onClick={selected.size === openList.length ? clearSel : selectAll}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", border: "none", color: C.ink, fontFamily: SANS, fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}>
              {selected.size === openList.length ? <CheckSquare size={15} /> : <Square size={15} />}
              {selected.size === openList.length ? tx.deselect : tx.selectAll}
            </button>
            <button type="button" onClick={() => changeRubric(openRubric)}
              style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "transparent", border: "none", color: C.gray, fontFamily: SANS, fontSize: 12, cursor: "pointer" }}>
              <X size={14} /> {tx.close}
            </button>
          </div>

          <div style={{ maxHeight: 320, overflowY: "auto" }}>
            {openList.map((a) => {
              const on = selected.has(a.id);
              return (
                <label key={a.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "9px 12px", borderBottom: `1px solid ${C.cream}`, cursor: "pointer", background: on ? "#fbf9f4" : C.white }}>
                  <input type="checkbox" checked={on} onChange={() => toggle(a.id)} style={{ width: 16, height: 16, accentColor: C.goldDark, flexShrink: 0 }} />
                  {a.image_url ? (
                    <img src={a.image_url} alt="" style={{ width: 34, height: 34, borderRadius: 5, objectFit: "cover", flexShrink: 0, border: `1px solid ${C.stone}` }} onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  ) : (
                    <span style={{ width: 34, height: 34, borderRadius: 5, background: C.cream, flexShrink: 0, border: `1px solid ${C.stone}` }} />
                  )}
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "block", fontSize: 13.5, color: C.ink, fontFamily: SERIF, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.title || tx.untitled}</span>
                    <span style={{ display: "block", fontSize: 12, color: C.gray, fontFamily: SANS, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {a.artist || tx.unknownArtist}{labelFor(a) ? ` · ${labelFor(a)}` : ""}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>

          {/* Barre d'actions groupées */}
          {selected.size > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 12px", background: C.cream, borderTop: `1px solid ${C.stone}`, flexWrap: "wrap" }}>
              <span style={{ fontSize: 12.5, color: C.ink, fontFamily: SANS, fontWeight: 600 }}>{tx.selectedN(selected.size)}</span>
              <span style={{ flex: 1 }} />
              <button type="button" disabled={busy} onClick={() => doCreate(false)}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", background: C.ink, color: C.white, border: "none", borderRadius: 7, fontFamily: SANS, fontSize: 12.5, fontWeight: 600, cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1 }}>
                <FolderPlus size={14} /> {tx.createMission}
              </button>
              {openRubric !== "ready" && (
                <button type="button" disabled={busy} onClick={() => doCreate(true)}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 12px", background: C.white, color: C.ink, border: `1px solid ${C.stone}`, borderRadius: 7, fontFamily: SANS, fontSize: 12.5, fontWeight: 600, cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1 }}>
                  <Bell size={14} /> {tx.relance}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
